import type { SimpleChoiceType } from '@/features/ChoicesLists/Simple/types';
import { computed, type ComputedRef } from 'vue';
import { CUSTOM_VALIDATION_ERROR_CLASS } from '@/components/form-elements/CustomFieldError/types';
import { scrollIntoView } from '@/utils/dom/dom';

interface UseForm {
  scrollToFirstError: (errors: Record<string, string[]>) => void;
  addField: (
    errorName: string,
    path: string,
    label: string | ComputedRef<string>,
    suffix?: string,
  ) => void;
  registerChoicesField: (path: string, choice: SimpleChoiceType) => void;
  fields: Set<string>;
  choicesFields: Map<string, SimpleChoiceType>;
  labelByPath: Map<string, ComputedRef<string>>;
  disabledPaths: Set<string>;
}

const fieldsSet: UseForm['fields'] = new Set();
const choicesFields: UseForm['choicesFields'] = new Map();
const labelByPath: UseForm['labelByPath'] = new Map();
const disabledPaths: UseForm['disabledPaths'] = new Set();

export const useForm = (): UseForm => {
  const scrollToFirstError = (errors: Record<string, string[]>) => {
    const errorKeys = Object.keys(errors);
    let foundFocus = false;

    fieldsSet.forEach((field: string) => {
      if (foundFocus) return;

      if (errorKeys.includes(field)) {
        const containerEl = document.querySelector(`div[data-v="${field}"]`);
        const textareaEl = document.querySelector(
          `textarea[data-v="${field}"]`,
        );

        if (containerEl || textareaEl) {
          foundFocus = true;
          scrollIntoView((containerEl ?? textareaEl) as HTMLElement);
        }
      }
    });

    // generic fallback selectors
    if (!foundFocus) {
      const selectors = [
        '.tab-with-validation-error',
        `.${CUSTOM_VALIDATION_ERROR_CLASS}`,
      ];

      selectors.forEach((selector) => {
        if (foundFocus) return;

        const possibleError = document.querySelector(selector);

        if (possibleError) {
          foundFocus = true;
          scrollIntoView(possibleError);
        }
      });
    }
  };

  const addField = (
    errorName: string,
    path: string,
    label: string | ComputedRef<string>,
  ) => {
    fieldsSet.add(errorName);

    labelByPath.set(
      path,
      computed(() => {
        return typeof label === 'string' ? label : label.value;
      }),
    );
  };

  const registerChoicesField = (path: string, choice: SimpleChoiceType) => {
    choicesFields.set(path, choice);
  };

  return {
    scrollToFirstError,
    addField,
    registerChoicesField,
    fields: fieldsSet,
    choicesFields,
    labelByPath,
    disabledPaths,
  };
};
